<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <v-skeleton-loader v-if="loading" />
    <div class="wo-container">
      <div class="wo-inner-container">
        <div class="wo-line" />
        <!-- Section Access Group -->
        <WorkOrderSection
          iconName="group_work"
          :showReportSection="false"
          :displayShowHideSection="false"
        >
          <template #child>
            <v-toolbar-title class="d-flex mt-2 mb-6">
              <span style="font-weight: 700 !important; color: #26384B; font-size: 21px">{{ $t('clients.accessGroups') }}</span>
            </v-toolbar-title>
            <v-row class="modalXMargin">
              <v-col
                cols="12"
                sm="8"
                class="modalColPadding"
              >
                <VAutocomplete
                  v-model="data.accessGroups"
                  :noChevron="!editAccessGroups"
                  outlined
                  :readonly="!editAccessGroups"
                  multiple
                  :label="$t('clients.accessGroups')"
                  itemText="name"
                  itemValue="id"
                  :items="memberAccessGroups"
                  data-cy="clientForm-autocomplete"
                  @change="updateData"
                />
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>
        <!-- Section Client Information -->
        <WorkOrderSection
          iconName="mdi-information"
          :showReportSection="false"
          :displayShowHideSection="false"
        >
          <template #child>
            <v-toolbar-title class="d-flex mt-2 mb-6">
              <span style="font-weight: 700 !important; color: #26384B; font-size: 21px">{{ $t('clients.clientInfo') }}</span>
            </v-toolbar-title>

            <v-col class="modalColPadding">
              <p class="modalXMargin titleModal">
                {{ $t('forms.labels.client') }}
              </p>
            </v-col>
            <v-row class="modalXMargin">
              <v-col
                cols="12"
                sm="8"
                class="modalColPadding"
              >
                <VTextField
                  v-model="data.name"
                  outlined
                  :label="$t('forms.labels.name')"
                  :rules="{ required: true, min: 1, max: 200 }"
                  @input="updateData"
                />
              </v-col>
              <v-col
                class="modalColPadding"
                cols="12"
                sm="4"
              >
                <VTextField
                  v-model="data.accountNumber"
                  outlined
                  :label="$t('forms.labels.accountNumber')"
                  :rules="{ required: true, min: 1, max: 20, regex: /^[a-zA-Z0-9-_& ]+$/ }"
                  @input="updateData"
                />
              </v-col>
            </v-row>

            <v-col class="modalColPadding">
              <p class="modalXMargin titleModal">
                {{ $t('forms.labels.phone') }}
              </p>
            </v-col>
            <v-row class="modalXMargin">
              <v-col
                class="modalColPadding"
                cols="12"
                sm="12"
                style="paddingBottom:30px"
              >
                <PhoneNumberInput
                  :refObserver="$refs.observer"
                  :required="true"
                  class="phonePlaceholder"
                  :rules="{ required: true }"
                  :value="data.phoneNumber.number"
                  :isPhoneNumberValid="data.isPhoneNumberValid"
                  data-cy="clientForm-phoneNumber"
                  @inputChanged="phoneInput"
                />
              </v-col>
            </v-row>

            <v-col class="modalColPadding">
              <p class="modalXMargin titleModal">
                {{ $t('forms.labels.address') }}
              </p>
            </v-col>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <AutocompleteAdresse
                  :disabled="disableAutoComplete"
                  @change="updateAddress"
                />
              </v-col>
            </v-row>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <AddressFormPart
                  :details="addressDetails"
                  :adressWithAutocomplete="true"
                  @change="onChangeAddress"
                />
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>
        <!-- Section Note -->
        <WorkOrderSection
          iconName="mdi-pencil"
          :showReportSection="false"
          :displayShowHideSection="false"
        >
          <template #child>
            <v-toolbar-title class="d-flex mt-2 mb-6">
              <span style="font-weight: 700 !important; color: #26384B; font-size: 21px">{{ $t('forms.labels.notes') }}</span>
            </v-toolbar-title>
            <v-row class="modalXMargin">
              <v-col class="modalColPadding">
                <VTextarea
                  v-model="data.notes"
                  outlined
                  :label="$t('forms.labels.textField')"
                  :rules="{ max: 200 }"
                  @input="updateData"
                />
              </v-col>
            </v-row>
          </template>
        </WorkOrderSection>
        <WorkOrderSection
          isLast
          iconName="settings"
          :displayShowHideSection="false"
        >
          <template #child>
            <div class="site-info-container">
              <v-toolbar-title class="header-info-sub">
                {{ $t('administration.configuration') }}
              </v-toolbar-title>
              <v-row class="modalXMargin">
                <v-col class="modalColPadding">
                  <VAutocomplete
                    v-if="hasPermission('clients.edit')"
                    v-model="data.defaultStandardFamily"
                    :label="$t('standardFamilies.labels.clientDefault')"
                    :items="availableStandardFamilies"
                    outlined
                    :multiple="false"
                    :clearable="true"
                    :loading="loading"
                    :disabled="disabledStandardFamily"
                    @input="updateData"
                  />
                </v-col>
              </v-row>
            </div>
          </template>
        </WorkOrderSection>
      </div>
    </div>
    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirtyChanged', dirty)"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('validChanged', valid)"
    />
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import AddressFormPart from '@/components/shared/forms-controls/AddressFormPart.vue';
import PhoneNumberInput from '@/components/shared/widgets/forms/PhoneNumberInput.vue';
import AutocompleteAdresse from '@/components/AutocompleteAdresse.vue';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import { isEmpty, isEqual } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import WorkOrderSection from '@/views/workOrders/overview/WorkOrderSection';
import roles from '@/constants/roles';

export default {
    components: {
        AddressFormPart,
        SlotScopeWatcher,
        VTextField,
        VTextarea,
        VAutocomplete,
        ValidationObserver,
        PhoneNumberInput,
        AutocompleteAdresse,
        WorkOrderSection,

    },
    props: {
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
        disableAutoComplete: {
            type: Boolean,
            default: false,
        },
        editAccessGroups: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            data: {
                name: null,
                accountNumber: null,
                phoneNumber: { number: '' },
                phoneExtension: null,
                streetNumber: null,
                address: null,
                province: null,
                country: null,
                city: null,
                postalCode: null,
                notes: null,
                active: null,
                initialStatus: true,
                isPhoneNumberValid: true,
                isFormValid: false,
                accessGroups: [],
                defaultStandardFamily: null,
            },
            loading: false,
            memberAccessGroups: [],
            availableStandardFamilies: [],
        };
    },
    computed: {
        ...mapGetters([
            'currentMember',
            'userLang',
            'teamMainLang',
            'hasRole',
            'hasPermission',
        ]),

        addressDetails() {
            return {
                address: this.data.address,
                city: this.data.city,
                country: this.data.country,
                postalCode: this.data.postalCode,
                province: this.data.province,
                streetNumber: this.data.streetNumber,
                appartmentNumber: this.data.appartmentNumber,
            };
        },
        disabledStandardFamily() {
            return !(this.hasRole(roles.SALES_REPRESENTATIVE.value)
          || this.hasRole(roles.SALES_MANAGER.value)
          || this.hasRole(roles.MANAGER.value)
          || this.hasRole(roles.ADMINISTRATOR.value));
        },
    },
    watch: {
        populateWith: {
            handler() {
                if (!this.populateWith.empty) {
                    this.data = this.populateWith;
                    this.data.initialStatus = this.populateWith.initialStatus;
                    this.data.phoneNumber = this.populateWith.phoneNumber ? this.populateWith.phoneNumber : { number: '' };
                }
            },
            immediate: true,
        },
        'data.clientAccessGroups': {
            deep: true,
            handler(newValue, oldValue) {
                const eq = isEqual(newValue, oldValue);
                if (!eq) {
                    this.updateData();
                }
            },
        },
    },
    async created() {
        this.data.initialStatus = this.populateWith.initialStatus;
        this.loading = true;
        const { data: { accessGroups } } = await this.$rq.getMemberAccess(this.currentMember.id);
        this.memberAccessGroups = accessGroups;
        this.getAvailableStandardFamilies();
        this.loading = false;
    },
    methods: {
        ...mapActions({
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),
        async updateData() {
            this.isFormValid = await this.$refs.observer.validate();
            // On clearing the default standard family, set it to null as back accept null for clearing the field
            if (this.data.defaultStandardFamily === undefined) this.data.defaultStandardFamily = null;
            this.$emit('sendData', { ...this.data, isFormValid: this.isFormValid, initialStatus: true });
        },
        async onChangeAddress(details) {
            this.data = {
                ...this.data,
                ...details,
            };
            await this.updateData();
        },
        phoneInput(number, payload) {
            this.data.phoneNumber = !number ? {} : payload;
            this.data.isPhoneNumberValid = payload.valid;
            this.updateData();
        },
        async getAvailableStandardFamilies() {
            this.loading = true;
            const { data } = await this.$rq.getStandardFamilies({ pagination: false });
            this.availableStandardFamilies = data.standardFamilies?.map(family => ({ name: family.name, id: family.id }));
            this.loading = false;
        },
        async updateAddress(adress) {
            if (!isEmpty(adress)) {
                this.data = {
                    ...this.data,
                    city: adress?.locality || adress?.sublocality_level_1 || adress?.administrative_area_level_3 || adress.administrative_area_level_2 || '',
                    country: adress?.country || '',
                    province: adress?.administrative_area_level_1 || '',
                    postalCode: adress?.postal_code || adress?.postal_code_prefix || '',
                    streetNumber: adress?.street_number || '',
                    address: adress?.route || '',
                };
                this.$emit('dirtyChange', true);
            }
            await this.updateData();
        },
        isAdminOrManager() {
            return this.hasRole('manager') || this.hasRole('administrator');
        },
    },
};
</script>

<style lang="scss" scoped>

  .phonePlaceholder ::placeholder {
    color: #FF5252 ;
    font-weight: 400;
    opacity: 1;
  }
  .phonePlaceholder {
    color: black
  }
</style>
